<template>
    <div class="guest-chat-widget">
        <transition name="fade">
            <b-card v-if="open" no-body class="guest-chat-panel shadow">
                <b-card-header class="bg-primary text-white py-2">
                    <strong>¿Necesitas ayuda?</strong>
                </b-card-header>
                <b-card-body class="guest-chat-messages p-3" v-chat-scroll>
                    <p v-if="!messages.length" class="text-muted text-center small mt-3 mb-0">
                        Cuéntanos en qué te podemos ayudar para completar tu registro o inicio de sesión, un asesor te responderá pronto.
                    </p>
                    <template v-for="(msg, idx) in messages">
                        <b-row :key="idx" v-if="msg.user_id === null" align-h="end" class="mx-0 mb-2">
                            <b-col cols="auto" class="pl-4 px-0">
                                <b-list-group-item variant="primary" class="rad-right text-right">
                                    <a v-if="msg.attachment" :href="'/' + msg.attachment" target="_blank">
                                        <img :src="'/' + msg.attachment" class="guest-chat-attachment"/>
                                    </a>
                                    <p class="mb-0" v-if="msg.message">{{ msg.message }}</p>
                                    <small>{{ msg.date }}</small>
                                </b-list-group-item>
                            </b-col>
                        </b-row>
                        <b-row :key="idx" v-else align-h="start" class="mx-0 mb-2">
                            <b-col cols="auto" class="pr-4 px-0">
                                <b-list-group-item variant="secondary" class="rad-left">
                                    <h6 class="mb-1"><b-icon icon="shield-fill-check"></b-icon> {{ msg.name }}</h6>
                                    <a v-if="msg.attachment" :href="'/' + msg.attachment" target="_blank">
                                        <img :src="'/' + msg.attachment" class="guest-chat-attachment"/>
                                    </a>
                                    <p class="mb-0" v-if="msg.message">{{ msg.message }}</p>
                                    <small>{{ msg.date }}</small>
                                </b-list-group-item>
                            </b-col>
                        </b-row>
                    </template>
                </b-card-body>
                <b-card-footer class="p-2">
                    <b-form-input
                        v-if="!started"
                        v-model="name"
                        placeholder="Tu nombre (opcional)"
                        :disabled="loading"
                        autocomplete="off"
                        class="mb-2"
                        size="sm"
                    ></b-form-input>
                    <div class="mb-2 small d-flex align-items-center" v-if="file">
                        <b-icon icon="image" class="mr-1"></b-icon>
                        <span class="text-truncate flex-grow-1">{{ file.name }}</span>
                        <b-button variant="link" size="sm" class="p-0 ml-1" @click="clearFile">
                            <b-icon icon="x-circle-fill"></b-icon>
                        </b-button>
                    </div>
                    <b-form @submit.stop.prevent="send">
                        <b-input-group>
                            <b-input-group-prepend>
                                <b-button variant="outline-secondary" :disabled="loading" @click="$refs.fileInput.click()">
                                    <b-icon icon="paperclip"></b-icon>
                                </b-button>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model="form.msg"
                                placeholder="Escribe tu consulta..."
                                :disabled="loading"
                                autocomplete="off"
                            ></b-form-input>
                            <b-input-group-append>
                                <b-button type="submit" variant="primary" :disabled="loading || (!form.msg && !file)">
                                    <b-spinner small type="grow" v-if="loading"></b-spinner>
                                    <b-icon icon="chat-left-text-fill" v-else></b-icon>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <input type="file" ref="fileInput" accept="image/png,image/jpeg" class="d-none" @change="onFile"/>
                    </b-form>
                </b-card-footer>
            </b-card>
        </transition>

        <b-button variant="primary" pill class="guest-chat-btn shadow" @click="toggle">
            <i class="material-icons">{{ open ? 'close' : 'support_agent' }}</i>
            <span class="ml-1" v-if="!open">¿Necesitas ayuda?</span>
            <b-badge v-if="unread && !open" variant="danger" pill class="guest-chat-badge">{{ unread }}</b-badge>
        </b-button>
    </div>
</template>

<script>
import axios from "axios";

const TOKEN_KEY = 'guestChatToken';

export default {
    name: "GuestChatWidget",
    data: () => ({
        open: false,
        loading: false,
        token: null,
        started: false,
        name: '',
        messages: [],
        seenCount: 0,
        unread: 0,
        file: null,
        form: {
            msg: ''
        },
        timeout: null,
        titleTimer: null,
        originalTitle: null
    }),
    mounted() {
        this.token = window.localStorage.getItem(TOKEN_KEY);
        this.started = !!this.token;
        if (this.token) {
            this.poll();
        }
    },
    beforeDestroy() {
        clearTimeout(this.timeout);
        this.stopAlert();
    },
    methods: {
        toggle() {
            this.open = !this.open;
            if (this.open) {
                this.unread = 0;
                this.seenCount = this.messages.length;
                this.stopAlert();
                if (this.token) {
                    this.getData();
                }
            }
        },
        async ensureToken() {
            if (this.token) { return this.token; }
            const res = await axios.post('/api/guest/chat/start');
            this.token = res.data.token;
            window.localStorage.setItem(TOKEN_KEY, this.token);
            return this.token;
        },
        getData() {
            if (!this.token) { return; }
            axios.get('/api/guest/chat/' + this.token).then((res) => {
                if (res.data.status === 'success') {
                    const previous = this.messages.length;
                    this.messages = res.data.messages;
                    if (this.open) {
                        this.seenCount = this.messages.length;
                    } else if (this.messages.length > this.seenCount) {
                        this.unread = this.messages.length - this.seenCount;
                        if (this.messages.length > previous) {
                            this.notify();
                        }
                    }
                }
            }).catch(() => {});
        },
        poll() {
            if (this.token) {
                this.getData();
            }
            this.timeout = setTimeout(this.poll, this.open ? 5000 : 20000);
        },
        onFile(e) {
            this.file = e.target.files.length ? e.target.files[0] : null;
        },
        clearFile() {
            this.file = null;
            this.$refs.fileInput.value = '';
        },
        async send() {
            if (!this.form.msg && !this.file) { return; }
            this.loading = true;

            try {
                const token = await this.ensureToken();

                let payload;
                if (this.file) {
                    payload = new FormData();
                    payload.append('msg', this.form.msg);
                    payload.append('name', this.name);
                    payload.append('image', this.file);
                } else {
                    payload = { msg: this.form.msg, name: this.name };
                }

                const res = await axios.post('/api/guest/chat/' + token + '/send', payload);
                if (res.data.status === 'success') {
                    this.started = true;
                    this.form.msg = '';
                    this.clearFile();
                    this.getData();
                    if (!this.timeout) { this.poll(); }
                } else {
                    this.$root.$bvToast.toast(res.data.msg, {
                        title: 'Notificación',
                        headerClass: 'd-none',
                        variant: res.data.status,
                        toaster: 'b-toaster-top-right',
                        autoHideDelay: 5000,
                        solid: true,
                        appendToast: true
                    });
                }
            } catch (e) {
                this.$root.$bvToast.toast('No se pudo enviar el mensaje, intenta nuevamente.', {
                    title: 'Notificación',
                    headerClass: 'd-none',
                    variant: 'error',
                    toaster: 'b-toaster-top-right',
                    autoHideDelay: 5000,
                    solid: true,
                    appendToast: true
                });
            }

            this.loading = false;
        },
        notify() {
            try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.frequency.value = 880;
                osc.connect(gain);
                gain.connect(ctx.destination);
                gain.gain.setValueAtTime(0.15, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
                osc.start();
                osc.stop(ctx.currentTime + 0.3);
            } catch (e) {}

            if (this.titleTimer || document.hasFocus()) { return; }
            this.originalTitle = document.title;
            let toggled = false;
            this.titleTimer = setInterval(() => {
                document.title = toggled ? this.originalTitle : '💬 Nuevo mensaje';
                toggled = !toggled;
            }, 1000);
            window.addEventListener('focus', this.stopAlert);
        },
        stopAlert() {
            if (!this.titleTimer) { return; }
            clearInterval(this.titleTimer);
            this.titleTimer = null;
            if (this.originalTitle) { document.title = this.originalTitle; }
            window.removeEventListener('focus', this.stopAlert);
        }
    }
}
</script>

<style scoped>
.guest-chat-widget {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 1050;
}
.guest-chat-btn {
    height: 40px;
    padding: 0 16px;
    position: relative;
    display: flex;
    align-items: center;
}
.guest-chat-btn .material-icons {
    font-size: 20px;
}
.guest-chat-badge {
    position: absolute;
    top: -6px;
    right: -6px;
}
.guest-chat-panel {
    position: absolute;
    right: 0;
    top: 48px;
    width: 320px;
    max-width: calc(100vw - 32px);
}
.guest-chat-messages {
    height: 340px;
    overflow-y: auto;
    overflow-x: hidden;
}
.guest-chat-attachment {
    max-width: 100%;
    max-height: 160px;
    border-radius: 6px;
    display: block;
    margin-bottom: 4px;
}
.rad-left {
    border-radius: 8px !important;
    border-top-left-radius: 0 !important;
}
.rad-right {
    border-radius: 8px !important;
    border-top-right-radius: 0 !important;
}
small, .small {
    font-size: 60%;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .15s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
